import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Calendar, Clock, Users, CheckCircle2, AlertTriangle, Plus, 
  FileText, ListTodo, ChevronRight, Loader2, Trash2, Pencil,
  CalendarDays, ClipboardList, Target, User, AlertCircle,
  CheckSquare, Square, ArrowRight, Eye, MoreVertical, Play, StopCircle, History, FileDown, RotateCcw
} from 'lucide-react';
import { StatusBadge, formatDate, isPast, isNear } from '../components/reunioes/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reunioes() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Lists
  const [reunioes, setReunioes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [ocorrenciasPassadas, setOcorrenciasPassadas] = useState([]);
  const [loadingPassadas, setLoadingPassadas] = useState(false);
  const [filtroPeriodoPassadas, setFiltroPeriodoPassadas] = useState('30');
  
  // Edit past occurrence modal
  const [showEditPassada, setShowEditPassada] = useState(false);
  const [ocorrenciaParaEditar, setOcorrenciaParaEditar] = useState(null);
  const [editPassada, setEditPassada] = useState({ data: '', horario: '', observacoes: '', status: 'realizada' });

  // Edit Ata modal (from Passadas tab)
  const [showEditAta, setShowEditAta] = useState(false);
  const [ataParaEditar, setAtaParaEditar] = useState(null);
  const [editAtaForm, setEditAtaForm] = useState({ resumo: '', decisoes: [] });

  // Delete reuniao confirm
  const [showDeleteReuniao, setShowDeleteReuniao] = useState(false);
  const [reuniaoParaExcluir, setReuniaoParaExcluir] = useState(null);

  // Lixeira (admin only)
  const [lixeira, setLixeira] = useState([]);
  const [loadingLixeira, setLoadingLixeira] = useState(false);
  const [showConfirmPermanente, setShowConfirmPermanente] = useState(false);
  const [reuniaoParaExcluirPermanente, setReuniaoParaExcluirPermanente] = useState(null);
  
  // Selected items
  const [selectedReuniao, setSelectedReuniao] = useState(null);
  const [selectedOcorrencia, setSelectedOcorrencia] = useState(null);
  
  // Modals
  const [showCreateReuniao, setShowCreateReuniao] = useState(false);
  const [showOcorrenciaDetail, setShowOcorrenciaDetail] = useState(false);
  const [showCreatePauta, setShowCreatePauta] = useState(false);
  const [showCreateAta, setShowCreateAta] = useState(false);
  const [showCreateTarefa, setShowCreateTarefa] = useState(false);
  const [showEditTarefa, setShowEditTarefa] = useState(false);
  const [selectedTarefa, setSelectedTarefa] = useState(null);
  
  // Form states
  const [novaReuniao, setNovaReuniao] = useState({
    nome: '',
    area: '',
    recorrencia: 'semanal',
    dia_semana: 'segunda',
    horario: '09:00',
    participantes: [],
    descricao: '',
    data_inicio: '',
    data_fim: ''
  });
  
  const [novaPauta, setNovaPauta] = useState({
    titulo: '',
    descricao: '',
    prioridade: 'media'
  });
  
  const [novaAta, setNovaAta] = useState({
    resumo: '',
    decisoes: []
  });
  
  const [novaTarefa, setNovaTarefa] = useState({
    descricao: '',
    responsavel_id: '',
    prazo: '',
    prioridade: 'media'
  });
  
  // Prazo change state
  const [showChangePrazo, setShowChangePrazo] = useState(false);
  const [tarefaParaAlterarPrazo, setTarefaParaAlterarPrazo] = useState(null);
  const [novoPrazo, setNovoPrazo] = useState('');
  const [justificativaPrazo, setJustificativaPrazo] = useState('');
  
  // Loading states
  const [saving, setSaving] = useState(false);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reunioes/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    }
  }, []);

  // Fetch meetings list
  const fetchReunioes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reunioes/reunioes`);
      setReunioes(response.data);
    } catch (error) {
      console.error('Error fetching reunioes:', error);
    }
  }, []);

  // Fetch users for selection
  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reunioes/usuarios-disponiveis`);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error fetching usuarios:', error);
    }
  }, []);

  // Fetch past occurrences (realizada/cancelada)
  const fetchOcorrenciasPassadas = useCallback(async (dias) => {
    setLoadingPassadas(true);
    try {
      const params = {};
      if (dias && dias !== 'todos') params.dias = dias;
      const response = await axios.get(`${API_URL}/api/reunioes/ocorrencias-passadas`, { params });
      setOcorrenciasPassadas(response.data);
    } catch (error) {
      console.error('Error fetching past occurrences:', error);
      toast.error('Erro ao carregar reuniões passadas');
    } finally {
      setLoadingPassadas(false);
    }
  }, []);

  // Export ata as PDF
  const handleExportAtaPdf = async (ocorrencia) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reunioes/ocorrencias/${ocorrencia.id}/ata-pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (ocorrencia.reuniao_nome || 'reuniao').replace(/\s+/g, '_');
      a.download = `ATA_${safeName}_${ocorrencia.data}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF da ata exportado!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Esta reunião ainda não possui ata para exportar');
      } else {
        toast.error('Erro ao exportar PDF da ata');
      }
    }
  };

  // Open edit ata modal for past occurrence
  const openEditAtaModal = (ocorrencia) => {
    setAtaParaEditar(ocorrencia);
    setEditAtaForm({
      resumo: ocorrencia.ata_resumo || '',
      decisoes: (ocorrencia.ata_decisoes || []).map(d => ({ descricao: d.descricao || '', gerar_tarefa: false }))
    });
    setShowEditAta(true);
  };

  // Save edited ata
  const handleSaveEditAta = async () => {
    if (!ataParaEditar || !ataParaEditar.ata_id) return;
    if (!editAtaForm.resumo || editAtaForm.resumo.trim().length < 5) {
      toast.error('Informe um resumo com pelo menos 5 caracteres');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/reunioes/atas/${ataParaEditar.ata_id}`, {
        resumo: editAtaForm.resumo,
        decisoes: editAtaForm.decisoes
          .filter(d => d.descricao && d.descricao.trim())
          .map(d => ({ descricao: d.descricao.trim(), gerar_tarefa: false }))
      });
      toast.success('Ata atualizada! Nova versão registrada.');
      setShowEditAta(false);
      setAtaParaEditar(null);
      fetchOcorrenciasPassadas(filtroPeriodoPassadas);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar ata');
    } finally {
      setSaving(false);
    }
  };

  // Add decision to edit ata form
  const addDecisaoEdit = () => {
    setEditAtaForm(prev => ({
      ...prev,
      decisoes: [...prev.decisoes, { descricao: '', gerar_tarefa: false }]
    }));
  };

  // Remove decision from edit ata form
  const removeDecisaoEdit = (index) => {
    setEditAtaForm(prev => ({
      ...prev,
      decisoes: prev.decisoes.filter((_, i) => i !== index)
    }));
  };

  // Update decision description in edit ata form
  const updateDecisaoEdit = (index, value) => {
    setEditAtaForm(prev => ({
      ...prev,
      decisoes: prev.decisoes.map((d, i) => i === index ? { ...d, descricao: value } : d)
    }));
  };

  // Check if user can delete a meeting (creator, responsavel or admin)
  const canDeleteReuniao = (reuniao) => {
    if (!user || !reuniao) return false;
    return (
      user.role === 'admin'
      || reuniao.created_by === user.id
      || reuniao.responsavel_id === user.id
    );
  };

  // Open delete reuniao confirm
  const openDeleteReuniaoConfirm = (reuniao) => {
    setReuniaoParaExcluir(reuniao);
    setShowDeleteReuniao(true);
  };

  // Confirm delete reuniao
  const handleDeleteReuniao = async () => {
    if (!reuniaoParaExcluir) return;
    setSaving(true);
    try {
      const response = await axios.delete(`${API_URL}/api/reunioes/reunioes/${reuniaoParaExcluir.id}`);
      const removidas = response.data?.ocorrencias_removidas ?? 0;
      const preservadas = response.data?.ocorrencias_passadas_preservadas ?? 0;
      if (preservadas > 0) {
        toast.success(
          `Reunião excluída! ${removidas} ocorrência(s) futura(s) removida(s). ${preservadas} passada(s) preservada(s) no histórico.`
        );
      } else {
        toast.success(`Reunião excluída! ${removidas} ocorrência(s) removida(s).`);
      }
      setShowDeleteReuniao(false);
      setReuniaoParaExcluir(null);
      await Promise.all([fetchReunioes(), fetchDashboard()]);
      if (activeTab === 'passadas') {
        fetchOcorrenciasPassadas(filtroPeriodoPassadas);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir reunião');
    } finally {
      setSaving(false);
    }
  };

  // Fetch lixeira (admin only)
  const fetchLixeira = useCallback(async () => {
    setLoadingLixeira(true);
    try {
      const response = await axios.get(`${API_URL}/api/reunioes/lixeira`);
      setLixeira(response.data);
    } catch (error) {
      console.error('Error fetching lixeira:', error);
      toast.error('Erro ao carregar lixeira');
    } finally {
      setLoadingLixeira(false);
    }
  }, []);

  // Restore reuniao from lixeira
  const handleRestaurarReuniao = async (reuniao) => {
    try {
      await axios.post(`${API_URL}/api/reunioes/reunioes/${reuniao.id}/restaurar`);
      toast.success(`Reunião "${reuniao.nome}" restaurada!`);
      await Promise.all([fetchLixeira(), fetchReunioes(), fetchDashboard()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar reunião');
    }
  };

  // Open confirm permanent delete
  const openConfirmPermanente = (reuniao) => {
    setReuniaoParaExcluirPermanente(reuniao);
    setShowConfirmPermanente(true);
  };

  // Permanently delete from lixeira
  const handleExcluirPermanente = async () => {
    if (!reuniaoParaExcluirPermanente) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/reunioes/reunioes/${reuniaoParaExcluirPermanente.id}/permanente`);
      toast.success('Reunião excluída permanentemente');
      setShowConfirmPermanente(false);
      setReuniaoParaExcluirPermanente(null);
      await Promise.all([fetchLixeira(), fetchOcorrenciasPassadas(filtroPeriodoPassadas)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir permanentemente');
    } finally {
      setSaving(false);
    }
  };

  // Open edit past occurrence modal
  const openEditPassadaModal = (ocorrencia) => {
    setOcorrenciaParaEditar(ocorrencia);
    setEditPassada({
      data: ocorrencia.data || '',
      horario: ocorrencia.horario || '',
      observacoes: ocorrencia.observacoes || '',
      status: ocorrencia.status || 'realizada'
    });
    setShowEditPassada(true);
  };

  // Save past occurrence edits
  const handleSaveEditPassada = async () => {
    if (!ocorrenciaParaEditar) return;
    if (!editPassada.data || !editPassada.horario) {
      toast.error('Data e horário são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/reunioes/ocorrencias/${ocorrenciaParaEditar.id}`, {
        data: editPassada.data,
        horario: editPassada.horario,
        observacoes: editPassada.observacoes,
        status: editPassada.status
      });
      toast.success('Reunião passada atualizada!');
      setShowEditPassada(false);
      setOcorrenciaParaEditar(null);
      fetchOcorrenciasPassadas(filtroPeriodoPassadas);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar reunião');
    } finally {
      setSaving(false);
    }
  };

  // Fetch occurrence details
  const fetchOcorrencia = async (ocorrenciaId) => {
    try {
      const response = await axios.get(`${API_URL}/api/reunioes/ocorrencias/${ocorrenciaId}`);
      setSelectedOcorrencia(response.data);
      setShowOcorrenciaDetail(true);
    } catch (error) {
      console.error('Error fetching ocorrencia:', error);
      toast.error('Erro ao carregar detalhes da reunião');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchReunioes(), fetchUsuarios()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchReunioes, fetchUsuarios]);

  // Load past occurrences when switching to that tab or filter changes
  useEffect(() => {
    if (activeTab === 'passadas') {
      fetchOcorrenciasPassadas(filtroPeriodoPassadas);
    }
  }, [activeTab, filtroPeriodoPassadas, fetchOcorrenciasPassadas]);

  // Load lixeira when switching to that tab (admin only)
  useEffect(() => {
    if (activeTab === 'lixeira' && user?.role === 'admin') {
      fetchLixeira();
    }
  }, [activeTab, user, fetchLixeira]);

  // Create new meeting
  const handleCreateReuniao = async () => {
    if (!novaReuniao.nome || !novaReuniao.area || novaReuniao.participantes.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (novaReuniao.recorrencia === 'unica' && !novaReuniao.data_inicio) {
      toast.error('Para reuniões únicas, informe a data');
      return;
    }
    if (novaReuniao.data_fim && novaReuniao.data_inicio && novaReuniao.data_fim < novaReuniao.data_inicio) {
      toast.error('A data final deve ser posterior à data inicial');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...novaReuniao,
        data_inicio: novaReuniao.data_inicio || null,
        data_fim: novaReuniao.data_fim || null
      };
      await axios.post(`${API_URL}/api/reunioes/reunioes`, payload);
      toast.success('Reunião criada com sucesso!');
      setShowCreateReuniao(false);
      setNovaReuniao({
        nome: '',
        area: '',
        recorrencia: 'semanal',
        dia_semana: 'segunda',
        horario: '09:00',
        participantes: [],
        descricao: '',
        data_inicio: '',
        data_fim: ''
      });
      fetchReunioes();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar reunião');
    } finally {
      setSaving(false);
    }
  };

  // Create pauta
  const handleCreatePauta = async () => {
    if (!novaPauta.titulo) {
      toast.error('Informe o título da pauta');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/reunioes/pautas`, {
        ocorrencia_id: selectedOcorrencia.id,
        ...novaPauta
      });
      toast.success('Pauta adicionada com sucesso!');
      setShowCreatePauta(false);
      setNovaPauta({ titulo: '', descricao: '', prioridade: 'media' });
      fetchOcorrencia(selectedOcorrencia.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar pauta');
    } finally {
      setSaving(false);
    }
  };

  // Create ata
  const handleCreateAta = async () => {
    if (!novaAta.resumo) {
      toast.error('Informe o resumo da reunião');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/reunioes/atas`, {
        ocorrencia_id: selectedOcorrencia.id,
        ...novaAta
      });
      toast.success('Ata criada com sucesso!');
      setShowCreateAta(false);
      setNovaAta({ resumo: '', decisoes: [] });
      fetchOcorrencia(selectedOcorrencia.id);
      
      // Mark as realized
      await axios.put(`${API_URL}/api/reunioes/ocorrencias/${selectedOcorrencia.id}`, {
        status: 'realizada'
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar ata');
    } finally {
      setSaving(false);
    }
  };

  // Create tarefa
  const handleCreateTarefa = async () => {
    if (!novaTarefa.descricao || !novaTarefa.responsavel_id || !novaTarefa.prazo) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/reunioes/tarefas`, {
        ocorrencia_id: selectedOcorrencia.id,
        ...novaTarefa
      });
      toast.success('Tarefa criada com sucesso!');
      setShowCreateTarefa(false);
      setNovaTarefa({ descricao: '', responsavel_id: '', prazo: '', prioridade: 'media' });
      fetchOcorrencia(selectedOcorrencia.id);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar tarefa');
    } finally {
      setSaving(false);
    }
  };

  // Update tarefa status
  const handleUpdateTarefaStatus = async (tarefaId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/reunioes/tarefas/${tarefaId}`, { status: newStatus });
      toast.success('Tarefa atualizada!');
      if (selectedOcorrencia) {
        fetchOcorrencia(selectedOcorrencia.id);
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Erro ao atualizar tarefa');
    }
  };

  // Delete ocorrencia
  const handleDeleteOcorrencia = async () => {
    if (!selectedOcorrencia) return;
    
    try {
      await axios.delete(`${API_URL}/api/reunioes/ocorrencias/${selectedOcorrencia.id}`);
      toast.success('Ocorrência excluída com sucesso!');
      setShowOcorrenciaDetail(false);
      setSelectedOcorrencia(null);
      fetchDashboard();
      fetchReunioes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir ocorrência');
    }
  };

  // Check if user is organizer of the meeting
  const isOrganizer = (reuniao) => {
    if (!user || !reuniao) return false;
    return user.role === 'admin' || reuniao.responsavel_id === user.id;
  };

  // Check if user can change task deadline
  const canChangePrazo = (tarefa) => {
    if (!user || !tarefa) return false;
    return user.role === 'admin' || tarefa.responsavel_id === user.id;
  };

  // Open prazo change modal
  const openChangePrazoModal = (tarefa) => {
    setTarefaParaAlterarPrazo(tarefa);
    setNovoPrazo(tarefa.prazo);
    setJustificativaPrazo('');
    setShowChangePrazo(true);
  };

  // Handle prazo change
  const handleChangePrazo = async () => {
    if (!tarefaParaAlterarPrazo || !novoPrazo || !justificativaPrazo) {
      toast.error('Preencha o novo prazo e a justificativa');
      return;
    }
    
    if (justificativaPrazo.trim().length < 10) {
      toast.error('A justificativa deve ter pelo menos 10 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/reunioes/tarefas/${tarefaParaAlterarPrazo.id}/prazo`, {
        novo_prazo: novoPrazo,
        justificativa: justificativaPrazo.trim()
      });
      toast.success('Prazo alterado com sucesso!');
      setShowChangePrazo(false);
      setTarefaParaAlterarPrazo(null);
      setNovoPrazo('');
      setJustificativaPrazo('');
      
      if (selectedOcorrencia) {
        fetchOcorrencia(selectedOcorrencia.id);
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar prazo');
    } finally {
      setSaving(false);
    }
  };

  // Start meeting
  const handleIniciarReuniao = async (ocorrenciaId) => {
    try {
      await axios.post(`${API_URL}/api/reunioes/ocorrencias/${ocorrenciaId}/iniciar`);
      toast.success('Reunião iniciada!');
      if (selectedOcorrencia?.id === ocorrenciaId) {
        fetchOcorrencia(ocorrenciaId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar reunião');
    }
  };

  // End meeting
  const handleFinalizarReuniao = async (ocorrenciaId) => {
    try {
      const response = await axios.post(`${API_URL}/api/reunioes/ocorrencias/${ocorrenciaId}/finalizar`);
      toast.success('Reunião finalizada!');
      if (response.data.ata_pendente) {
        toast.warning('Atenção: A ata ainda não foi preenchida!');
      }
      if (selectedOcorrencia?.id === ocorrenciaId) {
        fetchOcorrencia(ocorrenciaId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar reunião');
    }
  };

  // Revert meeting status to "agendada" (in case of mistake)
  const handleReverterReuniao = async (ocorrenciaId) => {
    const confirmMsg = 'Tem certeza que deseja reverter o status desta reunião para "Agendada"?\n\n' +
      'Isso vai desfazer o clique de Iniciar/Finalizar. Se a reunião foi finalizada e ' +
      'uma próxima ocorrência foi gerada automaticamente (sem ata/tarefas), ela também será removida.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.post(`${API_URL}/api/reunioes/ocorrencias/${ocorrenciaId}/reverter`);
      const removed = response.data?.proxima_ocorrencia_removida ?? 0;
      if (removed > 0) {
        toast.success(`Status revertido para Agendada. ${removed} ocorrência auto-gerada removida.`);
      } else {
        toast.success('Status revertido para Agendada');
      }
      if (selectedOcorrencia?.id === ocorrenciaId) {
        fetchOcorrencia(ocorrenciaId);
      }
      fetchDashboard();
      if (activeTab === 'passadas') {
        fetchOcorrenciasPassadas(filtroPeriodoPassadas);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reverter status');
    }
  };

  // Add decision to ata
  const addDecisao = () => {
    setNovaAta(prev => ({
      ...prev,
      decisoes: [...prev.decisoes, {
        descricao: '',
        gerar_tarefa: false,
        tarefa_responsavel_id: '',
        tarefa_prazo: '',
        tarefa_prioridade: 'media'
      }]
    }));
  };

  // Update decision
  const updateDecisao = (index, field, value) => {
    setNovaAta(prev => ({
      ...prev,
      decisoes: prev.decisoes.map((d, i) => i === index ? { ...d, [field]: value } : d)
    }));
  };

  // Remove decision
  const removeDecisao = (index) => {
    setNovaAta(prev => ({
      ...prev,
      decisoes: prev.decisoes.filter((_, i) => i !== index)
    }));
  };

  // Toggle participant
  const toggleParticipante = (userId) => {
    setNovaReuniao(prev => ({
      ...prev,
      participantes: prev.participantes.includes(userId)
        ? prev.participantes.filter(id => id !== userId)
        : [...prev.participantes, userId]
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reuniões</h1>
            <p className="text-slate-500 mt-1">
              Gestão de reuniões, decisões e tarefas
            </p>
          </div>
          <Button onClick={() => setShowCreateReuniao(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nova Reunião
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="reunioes" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Reuniões
            </TabsTrigger>
            <TabsTrigger value="tarefas" className="flex items-center gap-2">
              <ListTodo className="w-4 h-4" />
              Minhas Tarefas
            </TabsTrigger>
            <TabsTrigger value="passadas" className="flex items-center gap-2" data-testid="tab-passadas">
              <History className="w-4 h-4" />
              Passadas
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="lixeira" className="flex items-center gap-2" data-testid="tab-lixeira">
                <Trash2 className="w-4 h-4" />
                Lixeira
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboard && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Reuniões</p>
                          <p className="text-2xl font-bold">{dashboard.estatisticas.total_reunioes}</p>
                        </div>
                        <CalendarDays className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Tarefas Abertas</p>
                          <p className="text-2xl font-bold">{dashboard.estatisticas.total_tarefas_abertas}</p>
                        </div>
                        <ListTodo className="w-8 h-8 text-yellow-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={dashboard.estatisticas.tarefas_atrasadas > 0 ? 'border-red-200 bg-red-50' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Atrasadas</p>
                          <p className="text-2xl font-bold text-red-600">{dashboard.estatisticas.tarefas_atrasadas}</p>
                        </div>
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={dashboard.estatisticas.atas_pendentes > 0 ? 'border-orange-200 bg-orange-50' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Atas Pendentes</p>
                          <p className="text-2xl font-bold text-orange-600">{dashboard.estatisticas.atas_pendentes}</p>
                        </div>
                        <FileText className="w-8 h-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Próximas Reuniões */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Reuniões
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px]">
                        {dashboard.proximas_ocorrencias.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-4">Nenhuma reunião agendada</p>
                        ) : (
                          <div className="space-y-3">
                            {dashboard.proximas_ocorrencias.map(ocorrencia => (
                              <div 
                                key={ocorrencia.id}
                                className={`p-3 rounded-lg border transition-colors ${
                                  ocorrencia.status === 'em_andamento' 
                                    ? 'border-orange-300 bg-orange-50' 
                                    : 'bg-white hover:bg-slate-50'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div 
                                    className="flex-1 cursor-pointer"
                                    onClick={() => fetchOcorrencia(ocorrencia.id)}
                                  >
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-slate-900">{ocorrencia.reuniao_nome}</p>
                                      <StatusBadge status={ocorrencia.status} type="ocorrencia" />
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(ocorrencia.data)}
                                      <Clock className="w-3 h-3 ml-2" />
                                      {ocorrencia.horario}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {ocorrencia.status === 'agendada' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-green-600 border-green-300 hover:bg-green-50"
                                        onClick={(e) => { e.stopPropagation(); handleIniciarReuniao(ocorrencia.id); }}
                                        title="Iniciar Reunião"
                                      >
                                        <Play className="w-3 h-3 mr-1" />
                                        Iniciar
                                      </Button>
                                    )}
                                    {ocorrencia.status === 'em_andamento' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-red-600 border-red-300 hover:bg-red-50"
                                        onClick={(e) => { e.stopPropagation(); handleFinalizarReuniao(ocorrencia.id); }}
                                        title="Finalizar Reunião"
                                      >
                                        <StopCircle className="w-3 h-3 mr-1" />
                                        Finalizar
                                      </Button>
                                    )}
                                    {(ocorrencia.status === 'em_andamento' || ocorrencia.status === 'realizada') && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 w-7 p-0 text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                                        onClick={(e) => { e.stopPropagation(); handleReverterReuniao(ocorrencia.id); }}
                                        title="Reverter para Agendada (caso tenha clicado por engano)"
                                        data-testid={`btn-reverter-${ocorrencia.id}`}
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Minhas Tarefas */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ListTodo className="w-5 h-5" />
                        Minhas Tarefas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px]">
                        {dashboard.minhas_tarefas.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-4">Nenhuma tarefa pendente</p>
                        ) : (
                          <div className="space-y-2">
                            {dashboard.minhas_tarefas.map(tarefa => (
                              <div 
                                key={tarefa.id}
                                className={`p-3 rounded-lg border transition-colors ${
                                  tarefa.status === 'atrasada' ? 'border-red-200 bg-red-50' :
                                  isNear(tarefa.prazo) ? 'border-orange-200 bg-orange-50' : 'bg-white'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">{tarefa.descricao}</p>
                                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                      <span className="flex items-center gap-1">
                                        <CalendarDays className="w-3 h-3" />
                                        {tarefa.reuniao_nome}
                                      </span>
                                      <span className={`flex items-center gap-1 ${isPast(tarefa.prazo) ? 'text-red-600 font-medium' : ''}`}>
                                        <Clock className="w-3 h-3" />
                                        {formatDate(tarefa.prazo)}
                                        {canChangePrazo(tarefa) && tarefa.status !== 'concluida' && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-5 w-5 p-0 ml-1"
                                            onClick={(e) => { e.stopPropagation(); openChangePrazoModal(tarefa); }}
                                            title="Alterar prazo"
                                          >
                                            <Pencil className="w-3 h-3 text-blue-500" />
                                          </Button>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <StatusBadge status={tarefa.status} type="tarefa" />
                                    {tarefa.status !== 'concluida' && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-7 w-7 p-0"
                                        onClick={() => handleUpdateTarefaStatus(tarefa.id, 'concluida')}
                                      >
                                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Atas Pendentes Alert */}
                {dashboard.atas_pendentes.length > 0 && (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                        <AlertCircle className="w-5 h-5" />
                        Atas Pendentes
                      </CardTitle>
                      <CardDescription className="text-orange-600">
                        As seguintes reuniões foram realizadas mas ainda não possuem ata
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashboard.atas_pendentes.map(ocorrencia => (
                          <div 
                            key={ocorrencia.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-white border cursor-pointer hover:bg-slate-50"
                            onClick={() => fetchOcorrencia(ocorrencia.id)}
                          >
                            <div>
                              <p className="font-medium">{ocorrencia.reuniao_nome}</p>
                              <p className="text-sm text-slate-500">{formatDate(ocorrencia.data)}</p>
                            </div>
                            <Button size="sm" variant="outline">
                              <FileText className="w-4 h-4 mr-2" />
                              Criar Ata
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Reuniões Tab */}
          <TabsContent value="reunioes" className="space-y-4">
            <div className="grid gap-4">
              {reunioes.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <CalendarDays className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Nenhuma reunião encontrada</p>
                    <Button onClick={() => setShowCreateReuniao(true)} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Primeira Reunião
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                reunioes.map(reuniao => (
                  <Card key={reuniao.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{reuniao.nome}</h3>
                            <Badge variant="outline">{reuniao.area}</Badge>
                            {!reuniao.ativa && <Badge variant="secondary">Inativa</Badge>}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {reuniao.horario} - {reuniao.recorrencia}
                              {reuniao.dia_semana && ` (${reuniao.dia_semana})`}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {reuniao.participantes?.length || 0} participantes
                            </span>
                          </div>
                          {reuniao.proxima_ocorrencia && (
                            <div className="mt-2 text-sm">
                              <span className="text-blue-600 font-medium">
                                Próxima: {formatDate(reuniao.proxima_ocorrencia.data)} às {reuniao.proxima_ocorrencia.horario}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              if (reuniao.proxima_ocorrencia) {
                                fetchOcorrencia(reuniao.proxima_ocorrencia.id);
                              }
                            }}
                            disabled={!reuniao.proxima_ocorrencia}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Próxima
                          </Button>
                          {canDeleteReuniao(reuniao) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => openDeleteReuniaoConfirm(reuniao)}
                              data-testid={`btn-excluir-reuniao-${reuniao.id}`}
                              title={
                                reuniao.tem_passadas
                                  ? `Excluir a reunião. O histórico de ${reuniao.ocorrencias_passadas_count} ocorrência(s) passada(s) será preservado.`
                                  : 'Excluir reunião e todas as ocorrências futuras'
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tarefas Tab */}
          <TabsContent value="tarefas" className="space-y-4">
            {dashboard?.minhas_tarefas.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-300 mb-4" />
                  <p className="text-slate-500">Você não possui tarefas pendentes!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {dashboard?.minhas_tarefas.map(tarefa => (
                  <Card 
                    key={tarefa.id}
                    className={
                      tarefa.status === 'atrasada' ? 'border-red-200' :
                      isNear(tarefa.prazo) ? 'border-orange-200' : ''
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium">{tarefa.descricao}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-3 h-3" />
                              {tarefa.reuniao_nome}
                            </span>
                            <span className={`flex items-center gap-1 ${isPast(tarefa.prazo) ? 'text-red-600 font-medium' : ''}`}>
                              <Clock className="w-3 h-3" />
                              Prazo: {formatDate(tarefa.prazo)}
                              {canChangePrazo(tarefa) && tarefa.status !== 'concluida' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 ml-1"
                                  onClick={() => openChangePrazoModal(tarefa)}
                                  title="Alterar prazo"
                                >
                                  <Pencil className="w-3 h-3 text-blue-500" />
                                </Button>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={tarefa.prioridade} type="prioridade" />
                          <StatusBadge status={tarefa.status} type="tarefa" />
                          {tarefa.status !== 'concluida' && (
                            <Select
                              value={tarefa.status}
                              onValueChange={(value) => handleUpdateTarefaStatus(tarefa.id, value)}
                            >
                              <SelectTrigger className="w-[140px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="aberta">Aberta</SelectItem>
                                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                <SelectItem value="concluida">Concluída</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Passadas Tab */}
          <TabsContent value="passadas" className="space-y-4" data-testid="tab-passadas-content">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Reuniões Passadas
                    </CardTitle>
                    <CardDescription>
                      Reuniões realizadas ou canceladas. Apenas o criador da reunião ou administrador pode editá-las.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-500">Período:</Label>
                    <Select
                      value={filtroPeriodoPassadas}
                      onValueChange={setFiltroPeriodoPassadas}
                    >
                      <SelectTrigger className="w-[180px]" data-testid="filtro-periodo-passadas">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">Últimos 30 dias</SelectItem>
                        <SelectItem value="60">Últimos 60 dias</SelectItem>
                        <SelectItem value="90">Últimos 90 dias</SelectItem>
                        <SelectItem value="180">Últimos 180 dias</SelectItem>
                        <SelectItem value="todos">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingPassadas ? (
                  <div className="py-8 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
                  </div>
                ) : ocorrenciasPassadas.length === 0 ? (
                  <div className="py-8 text-center">
                    <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Nenhuma reunião passada encontrada</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ocorrenciasPassadas.map(ocorrencia => (
                      <div
                        key={ocorrencia.id}
                        className="p-4 rounded-lg border bg-white hover:bg-slate-50 transition-colors"
                        data-testid={`ocorrencia-passada-${ocorrencia.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => fetchOcorrencia(ocorrencia.id)}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-900">{ocorrencia.reuniao_nome}</p>
                              <StatusBadge status={ocorrencia.status} type="ocorrencia" />
                              {ocorrencia.tem_ata ? (
                                <Badge className="bg-green-100 text-green-700">Ata OK</Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-700">Sem Ata</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(ocorrencia.data)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {ocorrencia.horario}
                              </span>
                              {ocorrencia.reuniao_area && (
                                <span className="flex items-center gap-1">
                                  <Target className="w-3 h-3" />
                                  {ocorrencia.reuniao_area}
                                </span>
                              )}
                              {ocorrencia.reuniao_responsavel_nome && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {ocorrencia.reuniao_responsavel_nome}
                                </span>
                              )}
                            </div>
                            {ocorrencia.observacoes && (
                              <p className="text-xs text-slate-500 mt-2 italic">
                                {ocorrencia.observacoes}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); fetchOcorrencia(ocorrencia.id); }}
                              data-testid={`btn-ver-${ocorrencia.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                            {ocorrencia.tem_ata && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                onClick={(e) => { e.stopPropagation(); handleExportAtaPdf(ocorrencia); }}
                                data-testid={`btn-pdf-ata-${ocorrencia.id}`}
                                title="Exportar PDF da Ata"
                              >
                                <FileDown className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                            )}
                            {ocorrencia.pode_editar && ocorrencia.tem_ata && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-700 border-green-300 hover:bg-green-50"
                                onClick={(e) => { e.stopPropagation(); openEditAtaModal(ocorrencia); }}
                                data-testid={`btn-editar-ata-${ocorrencia.id}`}
                                title="Editar Ata"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Editar Ata
                              </Button>
                            )}
                            {ocorrencia.pode_editar && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                onClick={(e) => { e.stopPropagation(); openEditPassadaModal(ocorrencia); }}
                                data-testid={`btn-editar-passada-${ocorrencia.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                            )}
                            {ocorrencia.pode_editar && ocorrencia.status === 'realizada' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={(e) => { e.stopPropagation(); handleReverterReuniao(ocorrencia.id); }}
                                data-testid={`btn-reverter-passada-${ocorrencia.id}`}
                                title="Reverter para Agendada (clicou em finalizar por engano)"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reverter
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lixeira Tab (admin only) */}
          {user?.role === 'admin' && (
            <TabsContent value="lixeira" className="space-y-4" data-testid="tab-lixeira-content">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <Trash2 className="w-5 h-5" />
                    Lixeira — Reuniões Excluídas
                  </CardTitle>
                  <CardDescription>
                    Reuniões com histórico preservado que foram excluídas. Você pode restaurá-las ou excluí-las permanentemente (irreversível).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingLixeira ? (
                    <div className="py-8 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
                    </div>
                  ) : lixeira.length === 0 ? (
                    <div className="py-8 text-center">
                      <Trash2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">A lixeira está vazia</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {lixeira.map(reuniao => (
                        <div
                          key={reuniao.id}
                          className="p-4 rounded-lg border border-red-100 bg-red-50/30 hover:bg-red-50 transition-colors"
                          data-testid={`lixeira-item-${reuniao.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-slate-900">{reuniao.nome}</p>
                                <Badge variant="outline">{reuniao.area}</Badge>
                                {reuniao.ocorrencias_passadas_count > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700">
                                    {reuniao.ocorrencias_passadas_count} histórico
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {reuniao.horario} - {reuniao.recorrencia}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                  Excluída em {formatDate((reuniao.deleted_at || '').slice(0, 10))}
                                </span>
                                {reuniao.deleted_by_name && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    por {reuniao.deleted_by_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => handleRestaurarReuniao(reuniao)}
                                data-testid={`btn-restaurar-${reuniao.id}`}
                                title="Restaurar reunião"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restaurar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-100"
                                onClick={() => openConfirmPermanente(reuniao)}
                                data-testid={`btn-excluir-permanente-${reuniao.id}`}
                                title="Excluir permanentemente (irreversível)"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Create Reunião Modal */}
        <Dialog open={showCreateReuniao} onOpenChange={setShowCreateReuniao}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nova Reunião</DialogTitle>
              <DialogDescription>
                Configure uma nova reunião recorrente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Reunião *</Label>
                  <Input
                    value={novaReuniao.nome}
                    onChange={(e) => setNovaReuniao(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Ex: Daily Engenharia"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Área *</Label>
                  <Input
                    value={novaReuniao.area}
                    onChange={(e) => setNovaReuniao(prev => ({ ...prev, area: e.target.value }))}
                    placeholder="Ex: Engenharia, Produção, Comercial"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Recorrência</Label>
                  <Select
                    value={novaReuniao.recorrencia}
                    onValueChange={(value) => setNovaReuniao(prev => ({ ...prev, recorrencia: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diaria">Diária</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="quinzenal">Quinzenal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="unica">Única</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {novaReuniao.recorrencia !== 'diaria' && novaReuniao.recorrencia !== 'unica' && (
                <div className="space-y-2">
                  <Label>Dia da Semana</Label>
                  <Select
                    value={novaReuniao.dia_semana}
                    onValueChange={(value) => setNovaReuniao(prev => ({ ...prev, dia_semana: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="segunda">Segunda-feira</SelectItem>
                      <SelectItem value="terca">Terça-feira</SelectItem>
                      <SelectItem value="quarta">Quarta-feira</SelectItem>
                      <SelectItem value="quinta">Quinta-feira</SelectItem>
                      <SelectItem value="sexta">Sexta-feira</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div className="space-y-2">
                  <Label>Horário</Label>
                  <Input
                    type="time"
                    value={novaReuniao.horario}
                    onChange={(e) => setNovaReuniao(prev => ({ ...prev, horario: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    {novaReuniao.recorrencia === 'unica' ? 'Data da Reunião *' : 'Data Inicial'}
                  </Label>
                  <Input
                    type="date"
                    value={novaReuniao.data_inicio}
                    onChange={(e) => setNovaReuniao(prev => ({ ...prev, data_inicio: e.target.value }))}
                    data-testid="input-data-inicio"
                  />
                  <p className="text-xs text-slate-500">
                    {novaReuniao.recorrencia === 'unica'
                      ? 'Quando a reunião acontecerá'
                      : 'Quando começa a recorrência (opcional - usa próxima data se vazio)'}
                  </p>
                </div>
                {novaReuniao.recorrencia !== 'unica' && (
                  <div className="space-y-2">
                    <Label>Data Final (opcional)</Label>
                    <Input
                      type="date"
                      value={novaReuniao.data_fim}
                      onChange={(e) => setNovaReuniao(prev => ({ ...prev, data_fim: e.target.value }))}
                      min={novaReuniao.data_inicio || undefined}
                      data-testid="input-data-fim"
                    />
                    <p className="text-xs text-slate-500">
                      Após essa data, novas ocorrências não serão criadas
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={novaReuniao.descricao}
                  onChange={(e) => setNovaReuniao(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição ou objetivo da reunião"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Participantes * ({novaReuniao.participantes.length} selecionados)</Label>
                <ScrollArea className="h-[200px] border rounded-md p-2">
                  <div className="space-y-2">
                    {usuarios.map(u => (
                      <div 
                        key={u.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          novaReuniao.participantes.includes(u.id) ? 'bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => toggleParticipante(u.id)}
                      >
                        <Checkbox 
                          checked={novaReuniao.participantes.includes(u.id)}
                          onCheckedChange={() => toggleParticipante(u.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{u.role}</Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateReuniao(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateReuniao} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Reunião
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ocorrência Detail Modal */}
        <Dialog open={showOcorrenciaDetail} onOpenChange={setShowOcorrenciaDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedOcorrencia && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="flex items-center gap-2">
                      {selectedOcorrencia.reuniao_nome}
                      <StatusBadge status={selectedOcorrencia.status} type="ocorrencia" />
                    </DialogTitle>
                    <div className="flex items-center gap-2">
                      {isOrganizer(selectedOcorrencia.reuniao) &&
                        (selectedOcorrencia.status === 'em_andamento' || selectedOcorrencia.status === 'realizada') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-700 hover:bg-amber-50 border-amber-300"
                            onClick={() => handleReverterReuniao(selectedOcorrencia.id)}
                            title="Reverter para Agendada (caso tenha clicado por engano)"
                            data-testid="btn-reverter-detail"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reverter
                          </Button>
                        )}
                      {isOrganizer(selectedOcorrencia.reuniao) && selectedOcorrencia.status === 'agendada' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleDeleteOcorrencia}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Excluir
                        </Button>
                      )}
                    </div>
                  </div>
                  <DialogDescription>
                    {formatDate(selectedOcorrencia.data)} às {selectedOcorrencia.horario}
                    {selectedOcorrencia.revertida_de_status && (
                      <span className="ml-2 text-amber-600 text-xs">
                        ⚠️ Revertida de "{selectedOcorrencia.revertida_de_status}" por {selectedOcorrencia.revertida_por_nome}
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="pauta" className="mt-4">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="pauta">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Pauta ({selectedOcorrencia.pautas?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="ata">
                      <FileText className="w-4 h-4 mr-2" />
                      Ata
                    </TabsTrigger>
                    <TabsTrigger value="tarefas">
                      <ListTodo className="w-4 h-4 mr-2" />
                      Tarefas ({selectedOcorrencia.tarefas?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="anterior">
                      <Clock className="w-4 h-4 mr-2" />
                      Anterior
                    </TabsTrigger>
                  </TabsList>

                  {/* Pauta Tab */}
                  <TabsContent value="pauta" className="space-y-4">
                    {selectedOcorrencia.status === 'agendada' && (
                      <Button onClick={() => setShowCreatePauta(true)} size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Pauta
                      </Button>
                    )}
                    {selectedOcorrencia.pautas?.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Nenhum item de pauta</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedOcorrencia.pautas?.map((pauta, idx) => (
                          <Card key={pauta.id}>
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium">{idx + 1}. {pauta.titulo}</p>
                                  {pauta.descricao && (
                                    <p className="text-sm text-slate-500 mt-1">{pauta.descricao}</p>
                                  )}
                                  <p className="text-xs text-slate-400 mt-1">
                                    Solicitado por {pauta.solicitante_nome}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <StatusBadge status={pauta.prioridade} type="prioridade" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Ata Tab */}
                  <TabsContent value="ata" className="space-y-4">
                    {selectedOcorrencia.ata ? (
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">Resumo da Reunião</CardTitle>
                            <StatusBadge status={selectedOcorrencia.ata.status_aprovacao} type="aprovacao" />
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedOcorrencia.ata.resumo}</p>
                          
                          {selectedOcorrencia.ata.decisoes?.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2">Decisões:</h4>
                              <ul className="space-y-2">
                                {selectedOcorrencia.ata.decisoes.map((d, i) => (
                                  <li key={i} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                                    <CheckSquare className="w-4 h-4 text-green-600 mt-0.5" />
                                    <span className="text-sm">{d.descricao}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <p className="text-xs text-slate-400">
                            Criada por {selectedOcorrencia.ata.criado_por_nome} - Versão {selectedOcorrencia.ata.versao}
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-500 mb-4">Ata ainda não criada</p>
                        <Button onClick={() => setShowCreateAta(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Criar Ata
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  {/* Tarefas Tab */}
                  <TabsContent value="tarefas" className="space-y-4">
                    <Button onClick={() => setShowCreateTarefa(true)} size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Tarefa
                    </Button>
                    {selectedOcorrencia.tarefas?.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">Nenhuma tarefa criada</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedOcorrencia.tarefas?.map(tarefa => (
                          <Card key={tarefa.id} className={tarefa.status === 'atrasada' ? 'border-red-200' : ''}>
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium">{tarefa.descricao}</p>
                                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      {tarefa.responsavel_nome}
                                    </span>
                                    <span className={`flex items-center gap-1 ${isPast(tarefa.prazo) && tarefa.status !== 'concluida' ? 'text-red-600' : ''}`}>
                                      <Clock className="w-3 h-3" />
                                      {formatDate(tarefa.prazo)}
                                      {canChangePrazo(tarefa) && tarefa.status !== 'concluida' && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 w-5 p-0 ml-1"
                                          onClick={() => openChangePrazoModal(tarefa)}
                                          title="Alterar prazo"
                                        >
                                          <Pencil className="w-3 h-3 text-blue-500" />
                                        </Button>
                                      )}
                                    </span>
                                  </div>
                                  {tarefa.historico_prazo?.length > 0 && (
                                    <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Prazo alterado {tarefa.historico_prazo.length} vez(es)
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <StatusBadge status={tarefa.prioridade} type="prioridade" />
                                  <StatusBadge status={tarefa.status} type="tarefa" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Anterior Tab */}
                  <TabsContent value="anterior" className="space-y-4">
                    {selectedOcorrencia.ocorrencia_anterior ? (
                      <>
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Ata da Reunião Anterior</CardTitle>
                            <CardDescription>
                              {formatDate(selectedOcorrencia.ocorrencia_anterior.data)}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            {selectedOcorrencia.ocorrencia_anterior.ata ? (
                              <p className="text-slate-700 whitespace-pre-wrap">
                                {selectedOcorrencia.ocorrencia_anterior.ata.resumo}
                              </p>
                            ) : (
                              <p className="text-orange-600">Ata não preenchida</p>
                            )}
                          </CardContent>
                        </Card>

                        {selectedOcorrencia.ocorrencia_anterior.tarefas_pendentes?.length > 0 && (
                          <Card className="border-orange-200">
                            <CardHeader>
                              <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                                <AlertTriangle className="w-4 h-4" />
                                Tarefas Pendentes de Reuniões Anteriores
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {selectedOcorrencia.ocorrencia_anterior.tarefas_pendentes.map(tarefa => (
                                  <div key={tarefa.id} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                                    <div>
                                      <p className="font-medium text-sm">{tarefa.descricao}</p>
                                      <p className="text-xs text-slate-500">
                                        {tarefa.responsavel_nome} - Prazo: {formatDate(tarefa.prazo)}
                                      </p>
                                    </div>
                                    <StatusBadge status={tarefa.status} type="tarefa" />
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Não há reunião anterior</p>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Pauta Modal */}
        <Dialog open={showCreatePauta} onOpenChange={setShowCreatePauta}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Pauta</DialogTitle>
              <DialogDescription>
                Sugira um item para a pauta da reunião
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={novaPauta.titulo}
                  onChange={(e) => setNovaPauta(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Assunto a ser discutido"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={novaPauta.descricao}
                  onChange={(e) => setNovaPauta(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Detalhes adicionais"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select
                  value={novaPauta.prioridade}
                  onValueChange={(value) => setNovaPauta(prev => ({ ...prev, prioridade: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreatePauta(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreatePauta} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Ata Modal */}
        <Dialog open={showCreateAta} onOpenChange={setShowCreateAta}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Criar Ata</DialogTitle>
              <DialogDescription>
                Registre o resumo e as decisões da reunião
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Resumo da Reunião *</Label>
                <Textarea
                  value={novaAta.resumo}
                  onChange={(e) => setNovaAta(prev => ({ ...prev, resumo: e.target.value }))}
                  placeholder="Descreva os principais pontos discutidos"
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Decisões</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addDecisao}>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Decisão
                  </Button>
                </div>
                
                {novaAta.decisoes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    "Se não virou tarefa com responsável e prazo, não foi decidido."
                  </p>
                ) : (
                  <div className="space-y-4">
                    {novaAta.decisoes.map((decisao, index) => (
                      <Card key={index}>
                        <CardContent className="p-3 space-y-3">
                          <div className="flex items-start justify-between">
                            <Label className="text-sm">Decisão {index + 1}</Label>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500"
                              onClick={() => removeDecisao(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <Textarea
                            value={decisao.descricao}
                            onChange={(e) => updateDecisao(index, 'descricao', e.target.value)}
                            placeholder="Descreva a decisão"
                            rows={2}
                          />
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={decisao.gerar_tarefa}
                              onCheckedChange={(checked) => updateDecisao(index, 'gerar_tarefa', checked)}
                            />
                            <Label className="text-sm cursor-pointer">Gerar tarefa a partir desta decisão</Label>
                          </div>
                          
                          {decisao.gerar_tarefa && (
                            <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                              <div>
                                <Label className="text-xs">Responsável *</Label>
                                <Select
                                  value={decisao.tarefa_responsavel_id}
                                  onValueChange={(value) => updateDecisao(index, 'tarefa_responsavel_id', value)}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Selecione" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {usuarios.map(u => (
                                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Prazo *</Label>
                                <Input
                                  type="date"
                                  className="h-8"
                                  value={decisao.tarefa_prazo}
                                  onChange={(e) => updateDecisao(index, 'tarefa_prazo', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Prioridade</Label>
                                <Select
                                  value={decisao.tarefa_prioridade}
                                  onValueChange={(value) => updateDecisao(index, 'tarefa_prioridade', value)}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="baixa">Baixa</SelectItem>
                                    <SelectItem value="media">Média</SelectItem>
                                    <SelectItem value="alta">Alta</SelectItem>
                                    <SelectItem value="critica">Crítica</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateAta(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateAta} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Ata
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Tarefa Modal */}
        <Dialog open={showCreateTarefa} onOpenChange={setShowCreateTarefa}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Tarefa</DialogTitle>
              <DialogDescription>
                Crie uma tarefa com responsável e prazo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Textarea
                  value={novaTarefa.descricao}
                  onChange={(e) => setNovaTarefa(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="O que precisa ser feito"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Select
                  value={novaTarefa.responsavel_id}
                  onValueChange={(value) => setNovaTarefa(prev => ({ ...prev, responsavel_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prazo *</Label>
                  <Input
                    type="date"
                    value={novaTarefa.prazo}
                    onChange={(e) => setNovaTarefa(prev => ({ ...prev, prazo: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select
                    value={novaTarefa.prioridade}
                    onValueChange={(value) => setNovaTarefa(prev => ({ ...prev, prioridade: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="media">Média</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="critica">Crítica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTarefa(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateTarefa} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Prazo Modal */}
        <Dialog open={showChangePrazo} onOpenChange={setShowChangePrazo}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Alterar Prazo da Tarefa</DialogTitle>
              <DialogDescription>
                Informe o novo prazo e a justificativa para a alteração
              </DialogDescription>
            </DialogHeader>
            {tarefaParaAlterarPrazo && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm">{tarefaParaAlterarPrazo.descricao}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Prazo atual: {formatDate(tarefaParaAlterarPrazo.prazo)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Novo Prazo *</Label>
                  <Input
                    type="date"
                    value={novoPrazo}
                    onChange={(e) => setNovoPrazo(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Justificativa * (mínimo 10 caracteres)</Label>
                  <Textarea
                    value={justificativaPrazo}
                    onChange={(e) => setJustificativaPrazo(e.target.value)}
                    placeholder="Explique o motivo da alteração do prazo..."
                    rows={3}
                  />
                  <p className="text-xs text-slate-500">
                    {justificativaPrazo.length}/10 caracteres mínimos
                  </p>
                </div>
                {tarefaParaAlterarPrazo.historico_prazo?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-orange-600">Histórico de Alterações</Label>
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {tarefaParaAlterarPrazo.historico_prazo.map((h, i) => (
                        <div key={i} className="text-xs p-2 bg-orange-50 rounded">
                          <p><strong>{formatDate(h.prazo_anterior)}</strong> → <strong>{formatDate(h.novo_prazo)}</strong></p>
                          <p className="text-slate-600">"{h.justificativa}"</p>
                          <p className="text-slate-400">por {h.alterado_por_nome}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePrazo(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleChangePrazo} 
                disabled={saving || !novoPrazo || justificativaPrazo.trim().length < 10}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Alterar Prazo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Past Occurrence Modal */}
        <Dialog open={showEditPassada} onOpenChange={setShowEditPassada}>
          <DialogContent data-testid="modal-editar-passada">
            <DialogHeader>
              <DialogTitle>Editar Reunião Passada</DialogTitle>
              <DialogDescription>
                Ajuste os dados da reunião. Esta ação é restrita ao criador da reunião ou administrador.
              </DialogDescription>
            </DialogHeader>
            {ocorrenciaParaEditar && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm">{ocorrenciaParaEditar.reuniao_nome}</p>
                  {ocorrenciaParaEditar.reuniao_created_by_name && (
                    <p className="text-xs text-slate-500 mt-1">
                      Criada por: {ocorrenciaParaEditar.reuniao_created_by_name}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={editPassada.data}
                      onChange={(e) => setEditPassada({ ...editPassada, data: e.target.value })}
                      data-testid="edit-passada-data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Horário *</Label>
                    <Input
                      type="time"
                      value={editPassada.horario}
                      onChange={(e) => setEditPassada({ ...editPassada, horario: e.target.value })}
                      data-testid="edit-passada-horario"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editPassada.status}
                    onValueChange={(value) => setEditPassada({ ...editPassada, status: value })}
                  >
                    <SelectTrigger data-testid="edit-passada-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="realizada">Realizada</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={editPassada.observacoes}
                    onChange={(e) => setEditPassada({ ...editPassada, observacoes: e.target.value })}
                    placeholder="Adicione uma observação sobre esta reunião..."
                    rows={3}
                    data-testid="edit-passada-observacoes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditPassada(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEditPassada}
                disabled={saving || !editPassada.data || !editPassada.horario}
                data-testid="btn-salvar-edit-passada"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Ata Modal (from Passadas tab) */}
        <Dialog open={showEditAta} onOpenChange={setShowEditAta}>
          <DialogContent className="max-w-2xl" data-testid="modal-editar-ata">
            <DialogHeader>
              <DialogTitle>Editar Ata da Reunião</DialogTitle>
              <DialogDescription>
                Apenas o criador ou responsável da reunião pode editar. Cada alteração cria uma nova versão no histórico.
              </DialogDescription>
            </DialogHeader>
            {ataParaEditar && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm">{ataParaEditar.reuniao_nome}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatDate(ataParaEditar.data)} • {ataParaEditar.horario}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Resumo da Reunião *</Label>
                  <Textarea
                    value={editAtaForm.resumo}
                    onChange={(e) => setEditAtaForm({ ...editAtaForm, resumo: e.target.value })}
                    placeholder="Descreva o que foi discutido na reunião..."
                    rows={5}
                    data-testid="edit-ata-resumo"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Decisões</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addDecisaoEdit}
                      data-testid="btn-add-decisao-edit"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar Decisão
                    </Button>
                  </div>
                  {editAtaForm.decisoes.length === 0 ? (
                    <p className="text-sm text-slate-400 italic py-2">
                      Nenhuma decisão registrada. Clique em "Adicionar Decisão" para incluir.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {editAtaForm.decisoes.map((d, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex-1">
                            <Textarea
                              value={d.descricao}
                              onChange={(e) => updateDecisaoEdit(i, e.target.value)}
                              placeholder={`Decisão ${i + 1}`}
                              rows={2}
                              data-testid={`edit-ata-decisao-${i}`}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 h-9 w-9 p-0"
                            onClick={() => removeDecisaoEdit(i)}
                            data-testid={`btn-remove-decisao-edit-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Ao salvar, a versão atual da ata será preservada no histórico de versões.
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditAta(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEditAta}
                disabled={saving || !editAtaForm.resumo || editAtaForm.resumo.trim().length < 5}
                data-testid="btn-salvar-edit-ata"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Nova Versão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Reuniao Confirmation */}
        <AlertDialog open={showDeleteReuniao} onOpenChange={setShowDeleteReuniao}>          <AlertDialogContent data-testid="dialog-excluir-reuniao">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Excluir Reunião
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    Tem certeza que deseja excluir a reunião{' '}
                    <strong className="text-slate-900">{reuniaoParaExcluir?.nome}</strong>?
                  </p>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <p className="font-medium mb-1">🗑️ Esta ação vai remover:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>A reunião da lista ativa</li>
                      <li>Todas as ocorrências <strong>futuras (agendadas)</strong></li>
                      <li>Pautas das ocorrências futuras</li>
                    </ul>
                  </div>
                  {reuniaoParaExcluir?.tem_passadas && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                      <p className="font-medium mb-1">✅ O histórico será preservado:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{reuniaoParaExcluir?.ocorrencias_passadas_count} ocorrência(s) passada(s)</li>
                        <li>Atas e tarefas registradas</li>
                        <li>Continuam visíveis na aba "Passadas"</li>
                      </ul>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteReuniao}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-confirmar-excluir-reuniao"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Sim, excluir tudo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Permanent Delete Confirmation (from Lixeira) */}
        <AlertDialog open={showConfirmPermanente} onOpenChange={setShowConfirmPermanente}>
          <AlertDialogContent data-testid="dialog-excluir-permanente">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Excluir Permanentemente
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    Tem certeza que deseja excluir <strong className="text-slate-900">{reuniaoParaExcluirPermanente?.nome}</strong> permanentemente?
                  </p>
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
                    <p className="font-semibold">⚠️ Esta ação é IRREVERSÍVEL e vai remover:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs mt-1">
                      <li>A reunião e TODAS as ocorrências (passadas e futuras)</li>
                      <li>Todas as atas e seus históricos de versões</li>
                      <li>Todas as pautas e tarefas</li>
                    </ul>
                    <p className="text-xs mt-2 font-semibold">
                      Não será possível restaurar nem recuperar estes dados.
                    </p>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleExcluirPermanente}
                disabled={saving}
                className="bg-red-700 hover:bg-red-800 text-white"
                data-testid="btn-confirmar-excluir-permanente"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Sim, excluir permanentemente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
